<template>
  <div class="relative overflow-hidden">
    <template v-if="isSmAndUp">
      <TransitionFade>
        <UiButton
          v-show="showPrev"
          class="prev-gallery group !absolute left-3 z-10 flex size-10 items-center justify-center overflow-hidden !rounded-full !border-none !bg-transparent !p-0 inset-y-center lg:flex"
        >
          <div
            class="absolute inset-0 size-full rounded-full bg-primary opacity-45 backdrop-blur-lg transition-all"
          />
          <UiIcon
            name="chevron-down"
            class="size-4 rotate-90 text-icon-secondary transition-transform group-hover:-translate-x-px"
          />
        </UiButton>
      </TransitionFade>
      <UiSwiper>
        <Swiper
          :modules="[Navigation, Keyboard]"
          :navigation="{
            prevEl: '.prev-gallery',
            nextEl: '.next-gallery'
          }"
          :keyboard="{
            enabled: true,
            onlyInViewport: true
          }"
          watch-overflow
          :slides-per-view="'auto'"
          :space-between="8"
          class="rounded-lg pr-2 lg:pr-0"
          @transition-start="onSwiperEdge"
        >
          <SwiperSlide
            v-for="item in gallery"
            :key="item.object.id"
            class="h-[174px] w-fit lg:h-[440px]"
          >
            <button
              class="h-full w-fit cursor-zoom-in overflow-hidden !rounded-2xl"
              @click="emit('open', item.object.id)"
            >
              <UiPicture
                draggable="false"
                lazy
                :options="{ height: 440 * 2 }"
                :size-options="{ lg: { height: 174 * 1.5 } }"
                :src="item.object.url"
                class="h-full !w-full !max-w-max"
                :disabled-contain="true"
              />
            </button>
          </SwiperSlide>
        </Swiper>
      </UiSwiper>
      <TransitionFade>
        <UiButton
          v-show="showNext"
          class="next-gallery group !absolute right-3 z-10 flex size-10 items-center justify-center overflow-hidden !rounded-full !border-none !bg-transparent !p-0 inset-y-center lg:flex"
        >
          <div
            class="absolute inset-0 size-full rounded-full bg-primary opacity-45 backdrop-blur-lg transition-all"
          />
          <UiIcon
            name="chevron-down"
            class="size-4 -rotate-90 text-icon-secondary transition-transform group-hover:translate-x-px"
          />
        </UiButton>
      </TransitionFade>
    </template>
    <template v-else>
      <ul class="relative gap-1" :class="{ 'previews-grid': !isOnePreview }">
        <li
          v-for="(preview, index) in previews"
          :key="preview.object.id"
          class="w-full overflow-hidden rounded-lg"
          :class="[
            { 'row-span-2': !isOnePreview && index === 0 },
            !isOnePreview && roundedPreviews[index]
          ]"
          :style="{
            aspectRatio: isOnePreview
              ? aspectRatio.onePreview
              : aspectRatio.threePreviewsList[index]
          }"
        >
          <UiPicture
            draggable="false"
            lazy
            :options="{ width: 460 * 1.5 }"
            :src="preview.object.url"
            class="h-full !w-full object-cover"
            img-classes="object-cover w-full"
            :disabled-contain="true"
          />
        </li>
      </ul>
      <div
        class="absolute inset-0 z-10 flex size-full overflow-hidden"
        :class="{ 'opacity-0': !showMobileButton }"
        @click="emit('open', gallery[0].object.id)"
      >
        <button
          type="button"
          class="relative mb-2 ml-auto mr-2 mt-auto flex size-10 min-w-10 items-center justify-center !rounded-full !border-none"
        >
          <span class="absolute inset-0 rounded-full bg-primary opacity-45 backdrop-blur-lg" />
          <UiIcon name="gallery-preview" class="relative size-4 text-icon-secondary" />
        </button>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { TransitionFade } from '@morev/vue-transitions'
import type { Swiper as SwiperType } from 'swiper'
import { Navigation, Keyboard } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { computed, ref } from 'vue'
import { useBreakpoint } from '@/6_shared/lib'
import { UiButton, UiIcon, UiPicture, UiSwiper } from '@/6_shared/ui'
import { useGalleryPreview } from '../../lib'
import type { Event } from '../../model'

type PropType = {
  gallery: Event['gallery']
  galleryPreviews: Event['galleryPreviews']
}
type EmitType = {
  (e: 'open', objectId: number): void
}

const props = defineProps<PropType>()
const emit = defineEmits<EmitType>()
const {
  sm: [isSmAndUp]
} = useBreakpoint()

const showNext = ref(true)
const showPrev = ref(false)

const onSwiperEdge = (swiper: SwiperType) => {
  const { isEnd, isBeginning } = swiper

  showNext.value = !isEnd
  showPrev.value = !isBeginning
}

const { aspectRatio, roundedPreviews } = useGalleryPreview()

const showMobileButton = computed(() => props.gallery.length > 1)
const isOnePreview = computed(() => props.gallery.length < 3)
const previews = computed(() =>
  [...props.galleryPreviews].splice(0, props.gallery.length >= 3 ? 3 : 1)
)
</script>

<style scoped>
.previews-grid {
  display: grid;
  grid-template-columns: 2.39fr 1fr;
  grid-template-rows: 1fr 1fr;
}
</style>
